import React from 'react';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import {Button} from '@mui/material';
import {
  Delete, ErrorOutline, Logout, ShoppingCart, ShowChart,
} from '@mui/icons-material';
import {
  type QEPNotification, type QEPNotificationAction, QEPNotificationActionType, QEPSystemAction, type QEPSharePublishData, type LinkerDataType, QEPNotificationStatus, QEPNotificationType,
} from '@qep/types/src';
import {getQepSystem} from '@qep/shared/src';
import './NotificationCenter.scss';

type NotificationCardProps = {
  notification: QEPNotification<QEPNotificationActionType>;
  viewedNotifications: string[];
  flashingNotification: QEPNotification<QEPNotificationActionType> | undefined;
  onRemove: (id: string) => void;
  onReportIssue: () => void;
  onLogout: () => void;
  setFlashingNotification: (n: QEPNotification<QEPNotificationActionType> | undefined) => void;
  setViewedNotifications: (ids: string[]) => void;
};

function isQepNotificationStatus(status: unknown): status is QEPNotificationStatus {
  return Object.values(QEPNotificationStatus).includes(status as QEPNotificationStatus);
}

export const getStatusClass = (status: string) => {
  if (!isQepNotificationStatus(status)) {
    return '';
  }

  switch (status) {
    case QEPNotificationStatus.CRITICAL: {
      return 'browser-notification-critical';
    }

    case QEPNotificationStatus.HIGH: {
      return 'browser-notification-high';
    }

    case QEPNotificationStatus.MEDIUM: {
      return 'browser-notification-medium';
    }

    case QEPNotificationStatus.LOW: {
      return 'browser-notification-low';
    }

    case QEPNotificationStatus.SYSTEM: {
      return 'browser-notification-system';
    }
  }
};

function isQepNotificationType(type: unknown): type is QEPNotificationType {
  return Object.values(QEPNotificationType).includes(type as QEPNotificationType);
}

const getTypeIcon = (type: string) => {
  if (!isQepNotificationType(type)) {
    return null;
  }

  switch (type) {
    case QEPNotificationType.ORDER: {
      return <ShoppingCart className='browser-notification-type-icon' />;
    }

    case QEPNotificationType.ALGO: {
      return <ShowChart className='browser-notification-type-icon' />;
    }

    case QEPNotificationType.ALERT: {
      return <ErrorOutline className='browser-notification-type-icon' />;
    }

    case QEPNotificationType.SYSTEM: {
      return null;
    }
  }
};

const NotificationCard: React.FC<NotificationCardProps> = ({
  notification,
  viewedNotifications,
  flashingNotification,
  onRemove,
  onReportIssue,
  onLogout,
  setFlashingNotification,
  setViewedNotifications,
}) => (
  <MenuItem
    key={notification.id}
    className={`browser-notification-item ${getStatusClass(notification.status || QEPNotificationStatus.LOW)}`}
  >
    <div style={{display: 'flex', flex: 1, alignItems: 'center'}}>
      <div
        className={`browser-notification-priority-indicator ${getStatusClass(notification.status || QEPNotificationStatus.LOW)}`}
        style={{backgroundColor: 'var(--base-color)'}}
      />
      {getTypeIcon(notification.type)}
      <div>
        <Tooltip title={<span style={{fontSize: 14}}>{notification.title}</span>} arrow>
          <div className='browser-notification-title' style={{
            maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{notification.title}</div>
        </Tooltip>
        <div className='browser-notification-timestamp'>{new Date(notification.timestamp).toLocaleString()}</div>
        <Tooltip title={notification.longDescription} arrow>
          <div className='browser-notification-description'>{notification.shortDescription}</div>
        </Tooltip>
      </div>
      {notification.actions && <div className='browser-notification-actions' style={{marginLeft: 'auto', display: 'flex', gap: 8}}>
        {notification.actions.map((action: QEPNotificationAction<QEPNotificationActionType>, index: number) => {
          if (action.type === QEPNotificationActionType.SHARE) {
            return (<Button
              key={index}
              onClick={event => {
                event.stopPropagation();
                void getQepSystem().container.linker.publishOrOpenWindow((action.data as QEPSharePublishData<LinkerDataType>));
              }}
            >{action.label}</Button>);
          }

          if (action.type === QEPNotificationActionType.QEP_ACTION) {
            let icon: React.ReactNode | undefined;
            let label = '';

            switch (action.data) {
              case QEPSystemAction.LOGOUT: {
                icon = <Logout />;
                label = 'Logout';
                break;
              }

              case QEPSystemAction.OPEN_ISSUE_REPORTER: {
                icon = <ErrorOutline />;
                label = 'Report Issue';
                break;
              }

              default: {
                break;
              }
            }

            return (<Button
              key={index}
              onClick={event => {
                event.stopPropagation();
                if (action.data === QEPSystemAction.OPEN_ISSUE_REPORTER) {
                  onReportIssue();
                } else if (action.data === QEPSystemAction.LOGOUT) {
                  onLogout();
                }
              }}
              title={label}
            >{icon}</Button>);
          }

          return null;
        })}
      </div>}
    </div>
    <Delete
      className='browser-notification-close-button'
      onClick={() => {
        setViewedNotifications(viewedNotifications.filter(id => id !== String(notification.id)));
        onRemove(notification.id ?? '');
        if (flashingNotification?.id === notification.id) {
          setFlashingNotification(undefined);
        }
      }}
    />
  </MenuItem>
);

export default NotificationCard;
